import CloseIcon from '@mui/icons-material/Close'
import {
    Autocomplete,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ActionsFooter } from '../../../components/ActionsFooter'
import { DistanceField } from '../../../components/DistanceField'
import GenericModal from '../../../components/GenericModal'
import OperationsTable, {
    OperationsTableCell,
} from '../../../components/OperationsTable'
import { ProductAdvancedSearch } from '../../../components/ProductAdvancedSearch'
import { ProductSearchBar } from '../../../components/ProductSearchBar'
import { StyledPaper } from '../../../components/StyledPaper'
import { UnitTypeField } from '../../../components/UnitTypeField'
import { AccountInfo, useAccount } from '../../../context/account_context'
import { BranchInfo, useBranch } from '../../../context/branch_context'
import { CashierInfo, useCashier } from '../../../context/cashier_context'
import { useCoin } from '../../../context/coin_context'
import { ContactTypeEnum, useConfigs } from '../../../context/configs_context'
import { useLoading } from '../../../context/loading_context'
import { NoteInfo } from '../../../context/notes_context'
import { ProductInfo } from '../../../context/product_context'
import { useSnack } from '../../../context/snack_context'
import { listEmployee } from '../../../services/employee'
import { createTransferStock } from '../../../services/product'
import { listVehicles } from '../../../services/vehicle'
import { getDateInIsoFormat, getDateString, getProductStockInBranch } from '../../../utils/maks'
import { EmployeeInfo } from '../../Employee/Home'
import { VehicleInfo } from '../../Vehicle/Home'
import { ClientInfo } from '../../../context/client_context'
import { ClientSearchBar } from '../../../components/ClientSearchBar'
import { useTranslation } from 'react-i18next'
import { getSaleById } from '../../../services/sale'
import { StringField } from '../../../components/StringField'

export interface ProductInTransferStock {
    id?: number
    product: ProductInfo
    qnt: number
}

export enum TransferStockDestinationType {
    Branch = 0,
    Client = 1,
}

export interface TransferStockInfo {
    id?: number
    products: ProductInTransferStock[]
    originBranch: BranchInfo | null
    destinyBranch: BranchInfo | null
    client: ClientInfo | null
    destinationType: number
    employee: EmployeeInfo | null
    vehicle: VehicleInfo | null
    note: NoteInfo | null
    exitDate: Date | null
    arrivalDate: Date | null
    bill: string | null
    cashier: Partial<CashierInfo> | null
    account: Partial<AccountInfo> | null
    date?: string
    hour?: string
    isSummerTime?: boolean
    productCount?: number
    estimatedDistance: number
    cdc?: string
    qrCode?: string
    statusEletronicNote?: number
    logEletronicNote?: string
    discountStock: boolean
    saleId: number | null
    status: number
    annotation: string | null
}

export enum TransferStockStatus {
    Created = 0,
    Canceled = 1,
}

export const TransferStockStatusLabel: Record<number, string> = {
    0: 'Creado',
    1: 'Cancelado',
}

const transferStockDefault: TransferStockInfo = {
    account: null,
    bill: null,
    cashier: null,
    note: null,
    originBranch: null,
    destinyBranch: null,
    client: null,
    destinationType: TransferStockDestinationType.Branch,
    employee: null,
    vehicle: null,
    exitDate: null,
    arrivalDate: null,
    products: [],
    productCount: 0,
    estimatedDistance: 0,
    discountStock: true,
    saleId: null,
    status: TransferStockStatus.Created,
    annotation: null,
}

export interface TransferStockDestinationData {
    companyRegime: string
    companyEmissionDepartmentId: string
    companyEmissionDepartmentDescription: string
    companyEmissionCityId: string
    companyEmissionCityDescription: string
    companyEconomicActivityId: string
    companyEconomicActivityDescription: string
    companyEmissionHouseNumber: string
    companyEmissionDistrictId: string
    companyEmissionDistrictDescription: string
    name: string
    document: string
    address: string
    phone: string
    email: string
}

export function parseTransferStockDestinationData(
    transferStock: TransferStockInfo
): TransferStockDestinationData {
    if (transferStock.destinyBranch) {
        return {
            companyEconomicActivityDescription:
                transferStock.destinyBranch.companyEconomicActivityDescription,
            companyEconomicActivityId:
                transferStock.destinyBranch.companyEconomicActivityId,
            companyEmissionCityDescription:
                transferStock.destinyBranch.companyEmissionCityDescription,
            companyEmissionCityId:
                transferStock.destinyBranch.companyEmissionCityId,
            companyEmissionDepartmentDescription:
                transferStock.destinyBranch
                    .companyEmissionDepartmentDescription,
            companyEmissionDepartmentId:
                transferStock.destinyBranch.companyEmissionDepartmentId,
            companyEmissionDistrictDescription:
                transferStock.destinyBranch.companyEmissionDistrictDescription,
            companyEmissionDistrictId:
                transferStock.destinyBranch.companyEmissionDistrictId,
            companyEmissionHouseNumber:
                transferStock.destinyBranch.companyEmissionHouseNumber,
            companyRegime: transferStock.destinyBranch.companyRegime,
            name: transferStock.destinyBranch.name,
            document: transferStock.destinyBranch.companyDocument ?? '',
            address:
                transferStock.destinyBranch.contacts.find(
                    (c) => c.contactType.id === ContactTypeEnum.Address
                )?.value ?? '',
            email:
                transferStock.destinyBranch.contacts.find(
                    (c) => c.contactType.id === ContactTypeEnum.Email
                )?.value ?? '',
            phone:
                transferStock.destinyBranch.contacts.find(
                    (c) => c.contactType.id === ContactTypeEnum.Phone
                )?.value ?? '',
        }
    }

    if (transferStock.client) {
        return {
            companyEconomicActivityDescription:
                transferStock.client.companyEconomicActivityDescription,
            companyEconomicActivityId:
                transferStock.client.companyEconomicActivityId,
            companyEmissionCityDescription:
                transferStock.client.companyEmissionCityDescription,
            companyEmissionCityId: transferStock.client.companyEmissionCityId,
            companyEmissionDepartmentDescription:
                transferStock.client.companyEmissionDepartmentDescription,
            companyEmissionDepartmentId:
                transferStock.client.companyEmissionDepartmentId,
            companyEmissionDistrictDescription:
                transferStock.client.companyEmissionDistrictDescription,
            companyEmissionDistrictId:
                transferStock.client.companyEmissionDistrictId,
            companyEmissionHouseNumber:
                transferStock.client.companyEmissionHouseNumber,
            companyRegime: transferStock.client.companyRegime,
            name: transferStock.client.name,
            document: transferStock.client.document,
            address: transferStock.client.address,
            email: transferStock.client.emails[0],
            phone: transferStock.client.phoneNumbers[0],
        }
    }

    throw new Error('Destination entity not found')
}

export const TransferStock = () => {
    const { t } = useTranslation()
    const loading = useLoading()
    const snack = useSnack()
    const navigate = useNavigate()
    const [transferStock, setTransferStock] =
        useState<TransferStockInfo>(transferStockDefault)
    const { branches } = useBranch()
    const { accountInfo } = useAccount()
    const {
        loggedCashier,
        setOpenCashierHashModal,
        handleChangeCashierFromList,
        cashiers,
    } = useCashier()
    const { configs } = useConfigs()
    const { coins } = useCoin()
    const [openAdvancedSearchModal, setOpenAdvancedSearchModal] =
        useState(false)
    const [currentDate, setCurrentDate] = useState<Date>()
    const timeInterval = useRef<NodeJS.Timeout>()
    const theme: any = useTheme()
    const isXs = useMediaQuery(theme.breakpoints.down('sm'))
    const productRef = useRef<HTMLInputElement | null>(null)
    const [employees, setEmployees] = useState<EmployeeInfo[]>([])
    const [vehicles, setVehicles] = useState<VehicleInfo[]>([])

    const canChangeCashier =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Caixas:Modificar caixa')

    const canShowStock =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'listar estoque'
        ) !== -1

    const loadVehicles = async () => {
        loading.show()
        try {
            const res = await listVehicles()

            setVehicles(res)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const loadDataFromSaleId = async () => {
        if (!transferStock.saleId) return

        loading.show()
        try {
            const res = await getSaleById(transferStock.saleId, coins)

            setTransferStock({
                ...transferStock,
                products: res.products.map((p, index) => ({
                    product: p.product,
                    qnt: p.qnt,
                    id: index,
                })),
                productCount: res.products.length,
                destinationType: TransferStockDestinationType.Client,
                client: res.client?.id ? res.client : null,
                discountStock: false,
                exitDate: new Date(`${res.date}T${res.hour}Z`),
                originBranch: res.branch,
            })
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const searchEmployees = async (value: string) => {
        try {
            const res = await listEmployee(
                {
                    skip: 1,
                    take: 10,
                    search: value,
                },
                coins
            )
            setEmployees(res.data)
        } catch (error) {}
    }

    useEffect(() => {
        loadVehicles()

        timeInterval.current = setInterval(() => {
            const date = new Date()
            const offset = -(date.getTimezoneOffset() / 60)
            date.setHours(date.getHours() - offset)
            setCurrentDate(date)
        }, 1000)

        return () => {
            clearInterval(timeInterval.current)
        }
    }, [])

    useEffect(() => {
        if (configs.chooseCashier && transferStock.originBranch) {
            handleChangeCashierFromList(transferStock.originBranch?.id)
        }
    }, [transferStock, configs, cashiers])

    const validate = () => {
        if (!transferStock.originBranch) {
            snack.error('Seleccione una sucursal de origen')
            return false
        }
        if (
            transferStock.destinationType ===
                TransferStockDestinationType.Branch &&
            !transferStock.destinyBranch
        ) {
            snack.error('Seleccione una sucursal de destino')
            return false
        }
        if (
            transferStock.destinationType ===
                TransferStockDestinationType.Client &&
            !transferStock.client
        ) {
            snack.error('Seleccione un cliente')
            return false
        }
        if (transferStock.originBranch.id === transferStock.destinyBranch?.id) {
            snack.error(
                'La sucursal de origen no puede ser la misma del destino'
            )
            return false
        }
        if (!transferStock.employee) {
            snack.error('Seleccione un motorista')
            return false
        }
        if (!transferStock.vehicle) {
            snack.error('Seleccione una vehículo')
            return false
        }
        if (!transferStock.exitDate) {
            snack.error('Seleccione una fecha de salida')
            return false
        }
        if (!transferStock.arrivalDate) {
            snack.error('Seleccione una fecha de chegada')
            return false
        }
        if (
            transferStock.exitDate.valueOf() >
            transferStock.arrivalDate.valueOf()
        ) {
            snack.error('La fecha de salida debe ser anterior a la de chegada')
            return false
        }
        if (!transferStock.products.length) {
            snack.error('Seleccione ao menos un producto')
            return false
        }
        for (const product of transferStock.products) {
            if (!product.qnt) {
                snack.error(
                    `El producto ${product.product.name} está sin cantidad`
                )
                return false
            }
        }
        return true
    }

    const handleTransferStock = async () => {
        loading.show()
        try {
            const [exitDate, exitHour] = getDateInIsoFormat(
                transferStock.exitDate as Date,
                true
            ).split(' ')
            const [arrivalDate, arrivalHour] = getDateInIsoFormat(
                transferStock.arrivalDate as Date,
                true
            ).split(' ')

            await createTransferStock({
                data_chegada: arrivalDate,
                data_saida: exitDate,
                hora_chegada: arrivalHour,
                hora_saida: exitHour,
                id_filial_origem: transferStock.originBranch?.id as number,
                id_filial_destino: transferStock.destinyBranch?.id,
                id_cliente: transferStock.client?.id,
                flag_tipo_destino: transferStock.destinationType,
                id_funcionario: transferStock.employee?.id as number,
                id_veiculo: transferStock.vehicle?.id as number,
                products: transferStock.products.map((product) => ({
                    id_produto: product.product.id as number,
                    quantidade_produto: product.qnt,
                })),
                distancia_estimada: transferStock.estimatedDistance,
                flag_descontar_estoque: Number(transferStock.discountStock),
                id_venda: transferStock.saleId,
                anotacao: transferStock.annotation || null,
            })

            snack.success('Productos transferidos com sucesso!')
            navigate('/system/product/transfer-stock')
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const handleAddNewProducts = (products: ProductInfo[]) => {
        if (!transferStock.originBranch) {
            snack.error('Seleccione una sucursal de origen!')
            return
        }

        let count = transferStock.productCount ?? 0
        const newProducts = [...transferStock.products]

        for (const product of products) {
            const stock = getProductStockInBranch(
                product,
                transferStock.originBranch,
                true
            )

            const weight = product.weight
                ? product.weight
                : product.unitType === 'Un'
                  ? 1
                  : 0.01

            const foundProduct = newProducts.find(
                (p) => p.product.id === product.id
            )

            const usedStock = weight + (foundProduct?.qnt ?? 0)

            if (
                transferStock.discountStock &&
                usedStock > stock &&
                !configs.canTransferNegativeStock
            ) {
                snack.error(`El producto ${product.name} está sin stock!`)
                continue
            }

            if (foundProduct) {
                foundProduct.qnt += weight
            } else {
                count++
                newProducts.push({
                    id: count,
                    product: product,
                    qnt: weight,
                })
            }
        }

        setTransferStock((prev) => ({
            ...prev,
            products: newProducts,
            productCount: count,
        }))

        setTimeout(() => {
            productRef.current?.focus()
        }, 100)
    }

    return (
        <>
            {openAdvancedSearchModal && (
                <GenericModal
                    open
                    title="Búsqueda Avanzada"
                    handleClose={setOpenAdvancedSearchModal}
                    handleSubmit={() => {}}
                    textButton=""
                    fullScreen
                >
                    <ProductAdvancedSearch
                        returnProducts={(products: ProductInfo[]) => {
                            handleAddNewProducts(products)
                            setOpenAdvancedSearchModal(false)
                        }}
                        detailsButtonText="Selecionar"
                        localNavigation={true}
                        elevation={1}
                        filtersFromProps={{
                            branches: transferStock.originBranch
                                ? [transferStock.originBranch]
                                : [],
                        }}
                    />
                </GenericModal>
            )}
            <StyledPaper>
                <Box
                    sx={{
                        overflowX: 'auto',
                        pt: { xs: '20px', md: '10px' },
                    }}
                >
                    <Box
                        sx={{
                            minWidth: { xs: '800px', md: undefined },
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            spacing={'20px'}
                        >
                            <Stack
                                direction={'column'}
                                border={'1px solid var(--border-color)'}
                                borderRadius={'14px'}
                                boxShadow={
                                    '0 1px 3px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.04)'
                                }
                                bgcolor={'var(--surface)'}
                                p={'14px'}
                            >
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{ fontWeight: 'bold', mr: '5px' }}
                                    >
                                        {t('Usuário/Caja')}:{' '}
                                    </Box>
                                    <Box>
                                        {accountInfo?.name ?? '---'}
                                        {'/'}
                                    </Box>
                                    <Box
                                        component={'span'}
                                        sx={{
                                            color: canChangeCashier
                                                ? 'primary.main'
                                                : 'text.main',
                                            textDecoration: canChangeCashier
                                                ? 'underline'
                                                : 'none',
                                            cursor: canChangeCashier
                                                ? 'pointer'
                                                : 'auto',
                                        }}
                                        onClick={() => {
                                            if (canChangeCashier)
                                                setOpenCashierHashModal(true)
                                        }}
                                    >
                                        {loggedCashier?.name}
                                    </Box>
                                </Typography>
                                <Typography>
                                    <Box
                                        component="span"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {t('Fecha')}:{' '}
                                    </Box>{' '}
                                    {currentDate
                                        ? getDateString({
                                              dateObj: currentDate,
                                              configs: configs,
                                              isSummerTime:
                                                  configs.isSummerTime,
                                          })
                                        : '---'}
                                </Typography>
                                <FormControl sx={{ ml: '2px' }}>
                                    <RadioGroup
                                        row
                                        onChange={(e) => {
                                            const { value } = e.target
                                            setTransferStock((prev) => ({
                                                ...prev,
                                                destinationType: Number(value),
                                                destinyBranch: null,
                                                client: null,
                                            }))
                                        }}
                                        value={transferStock.destinationType}
                                    >
                                        <FormControlLabel
                                            value={
                                                TransferStockDestinationType.Branch
                                            }
                                            control={
                                                <Radio sx={{ p: '5px' }} />
                                            }
                                            label={t('Sucursal')}
                                        />
                                        <FormControlLabel
                                            value={
                                                TransferStockDestinationType.Client
                                            }
                                            control={
                                                <Radio sx={{ p: '5px' }} />
                                            }
                                            label={t('Cliente')}
                                        />
                                    </RadioGroup>
                                </FormControl>
                                <FormControlLabel
                                    label={t('Descontar stock')}
                                    control={
                                        <Checkbox
                                            checked={
                                                transferStock.discountStock
                                            }
                                            onChange={(e) => {
                                                setTransferStock((prev) => ({
                                                    ...prev,
                                                    discountStock:
                                                        e.target.checked,
                                                    products: prev.products.map(
                                                        (p) => ({
                                                            ...p,
                                                            qnt: 0,
                                                        })
                                                    ),
                                                }))
                                            }}
                                            sx={{ p: '5px', pl: '7px' }}
                                        />
                                    }
                                />
                            </Stack>
                            <Stack
                                direction={'column'}
                                flex={1}
                                justifyContent={'space-between'}
                                spacing={{ xs: '10px', lg: 0 }}
                            >
                                <Stack direction={'row'} spacing={'10px'}>
                                    <StringField
                                        label="Número venta (opcional)"
                                        value={
                                            transferStock.saleId?.toString() ??
                                            ''
                                        }
                                        setValue={(v) => {
                                            setTransferStock((prev) => ({
                                                ...prev,
                                                saleId: v ? Number(v) : null,
                                            }))
                                        }}
                                        sx={{ width: '33%' }}
                                        size="small"
                                        regex={/^\d+$/}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter')
                                                loadDataFromSaleId()
                                        }}
                                    />
                                    <FormControl
                                        sx={{
                                            width: '33%',
                                        }}
                                        size="small"
                                    >
                                        <InputLabel>{t('Origen')}</InputLabel>
                                        <Select
                                            label={t('Origen')}
                                            size="small"
                                            fullWidth
                                            value={
                                                transferStock.originBranch
                                                    ? transferStock.originBranch
                                                          .id
                                                    : ''
                                            }
                                            onChange={(e) => {
                                                const { value } = e.target
                                                if (!value) return
                                                const branch = branches.find(
                                                    (b) =>
                                                        b.id === Number(value)
                                                )
                                                if (!branch) return

                                                if (
                                                    transferStock.discountStock &&
                                                    !configs.canTransferNegativeStock
                                                )
                                                    setTransferStock(
                                                        (prev) => ({
                                                            ...prev,
                                                            originBranch:
                                                                branch,
                                                            products: [],
                                                            productCount: 0,
                                                        })
                                                    )
                                                else
                                                    setTransferStock(
                                                        (prev) => ({
                                                            ...prev,
                                                            originBranch:
                                                                branch,
                                                        })
                                                    )
                                            }}
                                        >
                                            {branches.map((branch) => (
                                                <MenuItem value={branch.id}>
                                                    {branch.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {transferStock.destinationType ===
                                    TransferStockDestinationType.Branch ? (
                                        <FormControl
                                            sx={{
                                                width: '33%',
                                            }}
                                            size="small"
                                        >
                                            <InputLabel>
                                                {t('Destino')}
                                            </InputLabel>
                                            <Select
                                                label={t('Destino')}
                                                size="small"
                                                fullWidth
                                                value={
                                                    transferStock.destinyBranch
                                                        ? transferStock
                                                              .destinyBranch.id
                                                        : ''
                                                }
                                                onChange={(e) => {
                                                    const { value } = e.target
                                                    if (!value) return
                                                    const branch =
                                                        branches.find(
                                                            (b) =>
                                                                b.id ===
                                                                Number(value)
                                                        )
                                                    if (branch)
                                                        setTransferStock(
                                                            (prev) => ({
                                                                ...prev,
                                                                destinyBranch:
                                                                    branch,
                                                            })
                                                        )
                                                }}
                                            >
                                                {branches.map((branch) => (
                                                    <MenuItem value={branch.id}>
                                                        {branch.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <ClientSearchBar
                                            client={transferStock.client}
                                            returnClient={(c) => {
                                                setTransferStock((prev) => ({
                                                    ...prev,
                                                    client: c,
                                                }))
                                            }}
                                            showAnonymousClient={false}
                                            sx={{
                                                width: '33%',
                                            }}
                                        />
                                    )}
                                </Stack>
                                <Stack direction={'row'} spacing={'10px'}>
                                    <Autocomplete
                                        options={employees}
                                        filterOptions={(p) => p}
                                        getOptionLabel={(p) => p.name}
                                        sx={{ width: '33%' }}
                                        value={transferStock.employee}
                                        onChange={(_, newEmployee) => {
                                            if (!newEmployee) return
                                            setTransferStock((prev) => ({
                                                ...prev,
                                                employee: newEmployee,
                                            }))
                                        }}
                                        blurOnSelect={true}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                onChange={(e) => {
                                                    const { value } = e.target
                                                    clearTimeout(
                                                        timeInterval.current
                                                    )
                                                    timeInterval.current =
                                                        setTimeout(() => {
                                                            searchEmployees(
                                                                value
                                                            )
                                                        }, 500)
                                                }}
                                                label="Motorista"
                                                sx={{ width: '100%' }}
                                                size={'small'}
                                            />
                                        )}
                                    />
                                    <FormControl
                                        sx={{
                                            width: '33%',
                                        }}
                                        size="small"
                                    >
                                        <InputLabel>Vehículo</InputLabel>
                                        <Select
                                            size="small"
                                            label="Vehículo"
                                            fullWidth
                                            value={
                                                transferStock.vehicle
                                                    ? transferStock.vehicle.id
                                                    : ''
                                            }
                                            onChange={(e) => {
                                                const { value } = e.target
                                                if (!value) return
                                                const vehicle = vehicles.find(
                                                    (v) =>
                                                        v.id === Number(value)
                                                )
                                                if (vehicle)
                                                    setTransferStock(
                                                        (prev) => ({
                                                            ...prev,
                                                            vehicle: vehicle,
                                                        })
                                                    )
                                            }}
                                        >
                                            {vehicles.map((vehicle) => (
                                                <MenuItem value={vehicle.id}>
                                                    {vehicle.brand}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <StringField
                                        label="Anotación"
                                        value={transferStock.annotation ?? ''}
                                        setValue={(v) =>
                                            setTransferStock((prev) => ({
                                                ...prev,
                                                annotation: v,
                                            }))
                                        }
                                        sx={{
                                            width: { xs: '33%' },
                                        }}
                                        size="small"
                                    />
                                </Stack>
                                <Stack direction={'row'} spacing={'10px'}>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDateFns}
                                    >
                                        <DateTimePicker
                                            label={t('Fecha salída')}
                                            sx={{
                                                width: { xs: '33%' },
                                            }}
                                            format="dd/MM/yyyy hh:mm:ss"
                                            ampm={false}
                                            value={transferStock.exitDate}
                                            onChange={(date) =>
                                                setTransferStock((prev) => ({
                                                    ...prev,
                                                    exitDate: date,
                                                }))
                                            }
                                            slotProps={{
                                                textField: { size: 'small' },
                                            }}
                                        />
                                        <DateTimePicker
                                            label={t('Fecha chegada')}
                                            sx={{
                                                width: { xs: '33%' },
                                            }}
                                            format="dd/MM/yyyy hh:mm:ss"
                                            ampm={false}
                                            value={transferStock.arrivalDate}
                                            onChange={(date) =>
                                                setTransferStock((prev) => ({
                                                    ...prev,
                                                    arrivalDate: date,
                                                }))
                                            }
                                            slotProps={{
                                                textField: { size: 'small' },
                                            }}
                                        />
                                        <DistanceField
                                            label="Distância estimada"
                                            value={
                                                transferStock.estimatedDistance
                                            }
                                            setValue={(v) =>
                                                setTransferStock((prev) => ({
                                                    ...prev,
                                                    estimatedDistance: v,
                                                }))
                                            }
                                            sx={{
                                                width: { xs: '33%' },
                                            }}
                                            size="small"
                                        />
                                    </LocalizationProvider>
                                </Stack>
                            </Stack>
                        </Stack>

                        <ProductSearchBar
                            returnProducts={handleAddNewProducts}
                            sx={{
                                width: '100%',
                                mt: '10px',
                            }}
                            ref={productRef}
                        />
                        <OperationsTable
                            variant="modern"
                            headers={[
                                { name: 'Nombre', width: '30%' },
                                { name: 'Stock', width: '20%' },
                                { name: 'Cantidad' },
                                { name: 'Nuevo Stock', width: '20%' },
                            ]}
                            data={transferStock.products}
                            renderCell={(
                                index,
                                product: ProductInTransferStock
                            ) => {
                                const originStock = getProductStockInBranch(
                                    product.product,
                                    transferStock.originBranch,
                                    true
                                )

                                const destinyStock = getProductStockInBranch(
                                    product.product,
                                    transferStock.destinyBranch,
                                    true
                                )

                                return (
                                    <>
                                        <OperationsTableCell
                                            sx={{
                                                backgroundColor:
                                                    index % 2 !== 0
                                                        ? 'var(--surface-soft)'
                                                        : 'var(--surface)',
                                            }}
                                        >
                                            <Stack
                                                direction={'row'}
                                                ml={'2px'}
                                                alignItems={'center'}
                                            >
                                                <Typography fontSize={'14px'}>
                                                    {product.product.parsedName}
                                                </Typography>
                                                <IconButton
                                                    onClick={() => {
                                                        const newProducts = [
                                                            ...transferStock.products,
                                                        ]
                                                        const index =
                                                            newProducts.findIndex(
                                                                (p) =>
                                                                    p.id ===
                                                                    product.id
                                                            )
                                                        if (index < 0) return

                                                        newProducts.splice(
                                                            index,
                                                            1
                                                        )

                                                        setTransferStock(
                                                            (prev) => ({
                                                                ...prev,
                                                                products:
                                                                    newProducts,
                                                            })
                                                        )
                                                    }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            </Stack>
                                        </OperationsTableCell>
                                        <OperationsTableCell
                                            sx={{
                                                backgroundColor:
                                                    index % 2 !== 0
                                                        ? 'var(--surface-soft)'
                                                        : 'var(--surface)',
                                            }}
                                        >
                                            <Stack
                                                direction={'column'}
                                                ml={'2px'}
                                            >
                                                {canShowStock ? (
                                                    <>
                                                        <Typography
                                                            fontSize={'14px'}
                                                        >
                                                            {
                                                                transferStock
                                                                    .originBranch
                                                                    ?.name
                                                            }
                                                            : {originStock}{' '}
                                                            {
                                                                product.product
                                                                    .unitType
                                                            }
                                                        </Typography>
                                                        <Typography
                                                            fontSize={'14px'}
                                                        >
                                                            {
                                                                transferStock
                                                                    .destinyBranch
                                                                    ?.name
                                                            }
                                                            : {destinyStock}{' '}
                                                            {
                                                                product.product
                                                                    .unitType
                                                            }
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography
                                                        fontSize={'14px'}
                                                    >
                                                        ---
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </OperationsTableCell>
                                        <OperationsTableCell
                                            sx={{
                                                backgroundColor:
                                                    index % 2 !== 0
                                                        ? 'var(--surface-soft)'
                                                        : 'var(--surface)',
                                            }}
                                        >
                                            <UnitTypeField
                                                label=""
                                                value={product.qnt}
                                                setValue={(v: number) => {
                                                    const newProducts = [
                                                        ...transferStock.products,
                                                    ]
                                                    const newProduct =
                                                        newProducts.find(
                                                            (p) =>
                                                                p.id ===
                                                                product.id
                                                        )
                                                    if (!newProduct) return
                                                    newProduct.qnt = v

                                                    setTransferStock(
                                                        (prev) => ({
                                                            ...prev,
                                                            products:
                                                                newProducts,
                                                        })
                                                    )
                                                }}
                                                sx={{
                                                    width: {
                                                        xs: '100%',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                    },
                                                }}
                                                size="small"
                                                unitType={
                                                    product.product.unitType
                                                }
                                                maxValue={
                                                    transferStock.discountStock &&
                                                    !configs.canTransferNegativeStock
                                                        ? originStock
                                                        : undefined
                                                }
                                            />
                                        </OperationsTableCell>
                                        <OperationsTableCell
                                            sx={{
                                                backgroundColor:
                                                    index % 2 !== 0
                                                        ? 'var(--surface-soft)'
                                                        : 'var(--surface)',
                                                borderRight: 'none',
                                            }}
                                        >
                                            <Stack
                                                direction={'column'}
                                                ml={'2px'}
                                            >
                                                {canShowStock ? (
                                                    <>
                                                        <Typography
                                                            fontSize={'14px'}
                                                        >
                                                            {
                                                                transferStock
                                                                    .originBranch
                                                                    ?.name
                                                            }
                                                            :{' '}
                                                            {transferStock.discountStock
                                                                ? `${
                                                                      originStock -
                                                                      product.qnt
                                                                  } ${
                                                                      product
                                                                          .product
                                                                          .unitType
                                                                  }`
                                                                : '---'}
                                                        </Typography>
                                                        <Typography
                                                            fontSize={'14px'}
                                                        >
                                                            {
                                                                transferStock
                                                                    .destinyBranch
                                                                    ?.name
                                                            }
                                                            :{' '}
                                                            {transferStock.discountStock
                                                                ? `${
                                                                      destinyStock +
                                                                      product.qnt
                                                                  } ${
                                                                      product
                                                                          .product
                                                                          .unitType
                                                                  }`
                                                                : '---'}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography
                                                        fontSize={'14px'}
                                                    >
                                                        ---
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </OperationsTableCell>
                                    </>
                                )
                            }}
                        />
                    </Box>
                </Box>
            </StyledPaper>

            <ActionsFooter
                cancelLabel="Volver"
                okLabel="Confirmar"
                cancelClick={() => navigate('/system/product/transfer-stock')}
                okClick={() => {
                    if (validate()) handleTransferStock()
                }}
            />
        </>
    )
}
